import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import api from '../../api/axios';
import { useAuth } from '../../contexts/AuthContext';

export default function SystemApprovals() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const auth = useAuth();
  
  // Data Grid State
  const [activeTab, setActiveTab] = useState('pending');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Modal State
  const [modalState, setModalState] = useState({ isOpen: false, type: null, requestId: null });
  const [noteTemplates, setNoteTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('manual');
  const [noteText, setNoteText] = useState('');
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState('');

  // Read More Note Modal
  const [noteModal, setNoteModal] = useState({ isOpen: false, text: '' });

  const fetchApps = useCallback(async (currentTab, currentPage, currentSearch, currentSortBy, currentSortDir) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/admin/apps', {
        params: {
          status: currentTab,
          page: currentPage,
          search: currentSearch,
          sort_by: currentSortBy,
          sort_dir: currentSortDir
        },
        headers: { Authorization: `Bearer ${auth?.accessToken}` }
      });
      
      const data = response.data;
      setRequests(data.data || []);
      setTotalPages(data.last_page || 1);
      setTotalItems(data.total || 0);
    } catch (err) {
      if (!axios.isCancel(err)) {
        console.error("Failed to fetch requests", err);
        setError('Failed to load applications.');
      }
    } finally {
      setLoading(false);
    }
  }, [auth?.accessToken]);

  useEffect(() => {
    if (auth?.isSessionReady && auth?.accessToken) {
        const timer = setTimeout(() => {
            fetchApps(activeTab, page, search, sortBy, sortDir);
        }, 300); // Debounce search
        return () => clearTimeout(timer);
    } else if (auth?.isSessionReady && !auth?.accessToken) {
        setLoading(false);
        setError('Not authenticated.');
    }
  }, [auth?.isSessionReady, auth?.accessToken, activeTab, page, search, sortBy, sortDir, fetchApps]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setPage(1); // Reset to first page on tab change
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDir('asc');
    }
    setPage(1);
  };

  const openActionModal = async (type, requestId) => {
    setModalState({ isOpen: true, type, requestId });
    setSelectedTemplateId('manual');
    setNoteText('');
    setModalError('');
    setModalLoading(true);
    setNoteTemplates([]);
    
    try {
      const res = await api.get(`/admin/action-note-templates/${type}`, {
        headers: { Authorization: `Bearer ${auth?.accessToken}` }
      });
      setNoteTemplates(res.data.templates || []);
    } catch (err) {
      console.error("Failed to fetch templates", err);
    } finally {
      setModalLoading(false);
    }
  };

  const closeActionModal = () => {
    setModalState({ isOpen: false, type: null, requestId: null });
  };

  const handleTemplateChange = (e) => {
    const val = e.target.value;
    setSelectedTemplateId(val);
    if (val === 'manual') {
      setNoteText('');
    } else {
      const tmpl = noteTemplates.find(t => t.id.toString() === val);
      if (tmpl) setNoteText(tmpl.note_text);
    }
  };

  const handleConfirmAction = async () => {
    if (!noteText || noteText.trim().length < 10) {
      setModalError('Note must be at least 10 characters long.');
      return;
    }
    if (noteText.length > 1000) {
      setModalError('Note cannot exceed 1000 characters.');
      return;
    }

    setModalError('');
    setModalLoading(true);

    try {
      await api.post(`/admin/apps/${modalState.requestId}/${modalState.type}`, { note: noteText }, {
        headers: { Authorization: `Bearer ${auth?.accessToken}` }
      });
      
      // Refresh current view
      fetchApps(activeTab, page, search, sortBy, sortDir);
      closeActionModal();
    } catch (err) {
      console.error(err);
      setModalError(err.response?.data?.message || `Failed to ${modalState.type} application.`);
    } finally {
      setModalLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  return (
    <div className="bg-white dark:bg-brand-dark text-slate-900 dark:text-brand-text min-h-full p-6 relative">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">
            System Approvals & Queue
          </h1>
          <p className="text-slate-600 dark:text-slate-400">
            Manage pending and processed OAuth integration requests from external client projects
          </p>
        </div>

        {/* Tabs & Search */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div className="flex bg-slate-100 dark:bg-slate-800/50 p-1 rounded-lg">
            <button
              onClick={() => handleTabChange('pending')}
              className={`px-4 py-2 text-sm font-semibold rounded-md transition-all ${
                activeTab === 'pending'
                  ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
              }`}
            >
              Pending
            </button>
            <button
              onClick={() => handleTabChange('approved')}
              className={`px-4 py-2 text-sm font-semibold rounded-md transition-all ${
                activeTab === 'approved'
                  ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
              }`}
            >
              Approved
            </button>
            <button
              onClick={() => handleTabChange('rejected_suspended')}
              className={`px-4 py-2 text-sm font-semibold rounded-md transition-all ${
                activeTab === 'rejected_suspended'
                  ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
              }`}
            >
              Rejected/Suspended
            </button>
          </div>

          <div className="w-full md:w-auto flex items-center gap-2">
            <input
              type="text"
              placeholder="Search apps or developers..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="w-full md:w-64 bg-slate-50 dark:bg-slate-900/80 border border-slate-300 dark:border-slate-700 rounded-lg px-4 py-2 text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
            <button
              onClick={() => fetchApps(activeTab, page, search, sortBy, sortDir)}
              title="Reload data"
              className="p-2 bg-slate-100 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors flex items-center justify-center"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              {/* Table Header */}
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-800 bg-slate-100 dark:bg-slate-900">
                  <th onClick={() => handleSort('application_name')} className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-800">
                    Project Title {sortBy === 'application_name' && (sortDir === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('owner_user_id')} className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-800">
                    Requested By {sortBy === 'owner_user_id' && (sortDir === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide">
                    Action Note
                  </th>
                  <th onClick={() => handleSort('created_at')} className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-800">
                    Date {sortBy === 'created_at' && (sortDir === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide">
                    Actions
                  </th>
                </tr>
              </thead>

              {/* Table Body */}
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-8 text-center text-slate-600 dark:text-slate-400">
                      Loading {activeTab} requests...
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-8 text-center text-red-600 dark:text-red-400">
                      {error}
                    </td>
                  </tr>
                ) : requests.length > 0 ? (
                  requests.map((request) => (
                    <tr
                      key={request.id}
                      className="border-b border-slate-200 dark:border-slate-800 hover:bg-slate-100 dark:hover:bg-slate-800/50 transition-colors"
                    >
                      <td className="px-6 py-4">
                        <span className="font-semibold text-slate-900 dark:text-white block">
                          {request.application_name}
                        </span>
                        <code className="bg-slate-200 dark:bg-slate-800 px-1 py-0.5 mt-1 rounded text-[10px] text-slate-600 dark:text-slate-400 font-mono block w-max">
                          {request.client_id}
                        </code>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-slate-700 dark:text-slate-300">
                          {request.owner_user_id}
                        </span>
                      </td>
                      <td className="px-6 py-4 max-w-xs">
                        {request.admin_action_note ? (
                          <div>
                            <p className="text-sm text-slate-600 dark:text-slate-400 line-clamp-2">
                              {request.admin_action_note}
                            </p>
                            {request.admin_action_note.length > 50 && (
                              <button 
                                onClick={() => setNoteModal({ isOpen: true, text: request.admin_action_note })}
                                className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline mt-1"
                              >
                                Read More
                              </button>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400 italic">No note</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-slate-600 dark:text-slate-400 block">
                          Cr: {formatDate(request.created_at)}
                        </span>
                        {request.approved_at && !request.suspended_at && (
                          <span className="text-xs text-emerald-600 dark:text-emerald-400 block">
                            Ap: {formatDate(request.approved_at)}
                          </span>
                        )}
                        {request.suspended_at && (
                          <span className="text-xs text-red-600 dark:text-red-400 block">
                            Su: {formatDate(request.suspended_at)}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-block px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                            request.suspended_at
                              ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                              : request.approved_at
                              ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300'
                              : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
                          }`}
                        >
                          {request.suspended_at ? 'Suspended/Rejected' : request.approved_at ? 'Live' : 'Pending'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap items-center gap-2">
                          {activeTab === 'pending' && (
                            <>
                              <button
                                onClick={() => openActionModal('approve', request.id)}
                                className="px-3 py-1 rounded text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
                              >
                                ✓ Approve
                              </button>
                              <button
                                onClick={() => openActionModal('reject', request.id)}
                                className="px-3 py-1 rounded text-sm font-semibold bg-red-600 text-white hover:bg-red-700 transition-colors"
                              >
                                ✗ Reject
                              </button>
                            </>
                          )}
                          {activeTab === 'approved' && (
                            <button
                              onClick={() => openActionModal('suspend', request.id)}
                              className="px-3 py-1 rounded text-sm font-semibold bg-orange-600 text-white hover:bg-orange-700 transition-colors"
                            >
                              ⏸ Suspend
                            </button>
                          )}
                          {activeTab === 'rejected_suspended' && (
                             <span className="text-xs text-slate-500 dark:text-slate-500">No Actions</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="px-6 py-8 text-center">
                      <p className="text-slate-600 dark:text-slate-400">
                        No {activeTab} requests at this time.
                      </p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 flex items-center justify-between">
              <span className="text-sm text-slate-600 dark:text-slate-400">
                Total: {totalItems} apps
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1 bg-slate-200 dark:bg-slate-800 rounded disabled:opacity-50 text-slate-700 dark:text-slate-300"
                >
                  Prev
                </button>
                <span className="px-3 py-1 text-sm font-medium text-slate-700 dark:text-slate-300">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-3 py-1 bg-slate-200 dark:bg-slate-800 rounded disabled:opacity-50 text-slate-700 dark:text-slate-300"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

      </div>

      {/* Action Modal */}
      {modalState.isOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl max-w-lg w-full overflow-hidden border border-slate-200 dark:border-slate-800">
            <div className="p-6">
              <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-1 capitalize">
                {modalState.type} Application
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
                Please provide a reason or note for this action. This will be recorded in the audit logs.
              </p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Action Note Template
                  </label>
                  <select
                    value={selectedTemplateId}
                    onChange={handleTemplateChange}
                    disabled={modalLoading}
                    className="w-full bg-slate-50 dark:bg-slate-900/80 border border-slate-300 dark:border-slate-700 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="manual">I'll write manually</option>
                    {noteTemplates.map(t => (
                      <option key={t.id} value={t.id}>
                        {t.note_text.substring(0, 50)}{t.note_text.length > 50 ? '...' : ''}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Note <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    disabled={modalLoading}
                    rows={4}
                    placeholder="Enter your note here..."
                    className="w-full bg-slate-50 dark:bg-slate-900/80 border border-slate-300 dark:border-slate-700 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  <div className="flex justify-between mt-1 text-xs text-slate-500">
                    <span>Min: 10, Max: 1000 characters</span>
                    <span className={noteText.length < 10 || noteText.length > 1000 ? 'text-red-500' : 'text-emerald-500'}>
                      {noteText.length} / 1000
                    </span>
                  </div>
                </div>

                {modalError && (
                  <p className="text-sm text-red-600 dark:text-red-400 font-medium">
                    {modalError}
                  </p>
                )}
              </div>
            </div>
            
            <div className="px-6 py-4 bg-slate-50 dark:bg-slate-800/50 flex justify-end gap-3 border-t border-slate-200 dark:border-slate-800">
              <button
                type="button"
                onClick={closeActionModal}
                disabled={modalLoading}
                className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmAction}
                disabled={modalLoading || noteText.length < 10 || noteText.length > 1000}
                className={`px-4 py-2 text-sm font-semibold text-white rounded-lg transition-colors ${
                  modalState.type === 'approve' 
                    ? 'bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50' 
                    : modalState.type === 'suspend' 
                    ? 'bg-orange-600 hover:bg-orange-700 disabled:opacity-50'
                    : 'bg-red-600 hover:bg-red-700 disabled:opacity-50'
                }`}
              >
                {modalLoading ? 'Processing...' : `Confirm ${modalState.type === 'approve' ? 'Approval' : modalState.type === 'suspend' ? 'Suspension' : 'Rejection'}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Note Read More Modal */}
      {noteModal.isOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/50">
           <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl max-w-md w-full overflow-hidden border border-slate-200 dark:border-slate-800">
             <div className="p-6">
               <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-4">Action Note</h3>
               <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap">
                 {noteModal.text}
               </p>
             </div>
             <div className="px-6 py-4 bg-slate-50 dark:bg-slate-800/50 flex justify-end border-t border-slate-200 dark:border-slate-800">
                <button
                  type="button"
                  onClick={() => setNoteModal({ isOpen: false, text: '' })}
                  className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors"
                >
                  Close
                </button>
             </div>
           </div>
        </div>
      )}
    </div>
  );
}
